import { useEffect, useMemo } from 'react'
import { usePublicIpQuota } from '@/hooks/usePublicIpQuota'
import { useVmSizeCheck } from '@/hooks/useVmSizeCheck'
import { INFRA_SUB_STEPS, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { InfraProduct, InfraSubStep } from '@/types/infrastructure'
import { SUB_STEP_LABELS, validateInfrastructure } from '@/utils/infraValidation'
import { Button } from '@/components/common/Button'
import styles from './Infrastructure.module.css'
import { AddOnsStep } from './AddOnsStep'
import { LevelStep } from './LevelStep'
import { LocationStep } from './LocationStep'
import { ProductChip } from './ProductChip'
import { ServersStep } from './ServersStep'
import { VmSizeBanner } from './VmSizeBanner'
import clsx from 'clsx'

interface InfrastructureFlowProps {
  /** The product chosen in the wizard's first step. Every lookup in the flow is for this product. */
  product: InfraProduct
  /** From the Customer Code field of the Environment section. */
  customerNumber: number | null
  /** IP addresses the RDS server needs in its subnet: one per RDS VM. Left out, it is the number of RDS VMs the user chose in the Servers step. */
  requiredIps?: number
}

/**
 * The Infrastructure flow: Location -> Level -> Servers -> Add-ons, with the product shown in the header throughout.
 * "Next" is disabled until the current step is valid, and Back never loses what was entered. The Review step is the
 * wizard's own final step, which shows this flow's summary.
 */
export function InfrastructureFlow({ product, customerNumber, requiredIps }: InfrastructureFlowProps) {
  const data = useInfrastructureWizard()
  const { setProduct, setCustomerNumber, setSubStep, dismissNotice } = data
  const synced = data.product?.slug === product.slug && data.product?.line === product.line

  // Keep the store in step with what the wizard knows: the product (and its line) and the Customer Code.
  useEffect(() => {
    setProduct({ slug: product.slug, name: product.name, ...(product.line && { line: product.line }) })
  }, [product.slug, product.name, product.line, setProduct])
  useEffect(() => {
    setCustomerNumber(customerNumber)
  }, [customerNumber, setCustomerNumber])

  // A VM size nothing can replace in the chosen region (not offered, restricted, or no quota left) stops the Servers step.
  const { problems: vmSizeProblems } = useVmSizeCheck(requiredIps)
  // A public IP answered Yes needs the quota to be there: while it is being asked, is short, or cannot be read, the Servers step waits too.
  const { problems: publicIpProblems } = usePublicIpQuota()
  const validation = useMemo(
    () => validateInfrastructure(data, { requiredIps, vmSizeProblems, publicIpProblems }),
    [data, requiredIps, vmSizeProblems, publicIpProblems],
  )
  const problemsOf: Record<InfraSubStep, string[]> = { location: validation.location, level: validation.level, servers: validation.servers, addons: validation.addons }
  const index = INFRA_SUB_STEPS.indexOf(data.subStep)
  const current = problemsOf[data.subStep]
  // A step can be reached once every step before it is valid.
  const firstInvalid = INFRA_SUB_STEPS.findIndex((step) => problemsOf[step].length > 0)
  const furthest = firstInvalid === -1 ? INFRA_SUB_STEPS.length - 1 : firstInvalid

  return (
    <section className={styles.flow} aria-label="Infrastructure" data-testid="infrastructure-flow">
      <div className={styles.header}>
        <h3 className={styles.title}>Infrastructure</h3>
        <ProductChip product={synced ? data.product : product} />
      </div>

      {data.notice && (
        <div className={styles.notice} role="status">
          <span>{data.notice}</span>
          <button type="button" className={styles.linkButton} onClick={dismissNotice}>
            Dismiss
          </button>
        </div>
      )}

      <ol className={styles.stepper} aria-label="Infrastructure steps">
        {INFRA_SUB_STEPS.map((step, position) => {
          const done = position < index && problemsOf[step].length === 0
          return (
            <li key={step}>
              <button
                type="button"
                className={clsx(styles.stepperButton, step === data.subStep && styles.stepperActive, done && styles.stepperDone)}
                aria-current={step === data.subStep ? 'step' : undefined}
                disabled={position > furthest}
                onClick={() => setSubStep(step)}
              >
                <span className={styles.stepperNumber}>{done ? '✓' : position + 1}</span>
                {SUB_STEP_LABELS[step]}
              </button>
            </li>
          )
        })}
      </ol>

      {/* Which VM series will really be built in the chosen region: the nearest series of the same size where one is not available. */}
      {synced && <VmSizeBanner requiredIps={requiredIps} />}

      {synced && (
        <div className={styles.section} style={{ borderTop: 'none', paddingTop: 0 }}>
          {data.subStep === 'location' && <LocationStep />}
          {data.subStep === 'level' && <LevelStep />}
          {data.subStep === 'servers' && <ServersStep requiredIps={requiredIps} />}
          {data.subStep === 'addons' && <AddOnsStep />}
        </div>
      )}

      {current.length > 0 && (
        <div aria-live="polite">
          <span className={styles.status}>To continue:</span>
          <ul className={styles.problems}>
            {current.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.actions}>
        <Button type="button" variant="secondary" disabled={index === 0} onClick={() => setSubStep(INFRA_SUB_STEPS[index - 1])}>
          Back
        </Button>
        {index < INFRA_SUB_STEPS.length - 1 ? (
          <Button type="button" disabled={current.length > 0} onClick={() => setSubStep(INFRA_SUB_STEPS[index + 1])}>
            Next: {SUB_STEP_LABELS[INFRA_SUB_STEPS[index + 1]]}
          </Button>
        ) : (
          <span className={styles.status} role="status">
            {validation.valid ? 'Infrastructure details are complete.' : 'Complete the steps above to continue.'}
          </span>
        )}
      </div>
    </section>
  )
}
